<script setup>
import { ref, computed } from 'vue';
import BaseAttachmentBubble from './BaseAttachment.vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../provider.js';

const { attachments } = useMessageContext();
const { t } = useI18n();

const attachment = computed(() => {
  return attachments.value[0];
});

const lat = computed(() => {
  return attachment.value.coordinatesLat;
});
const long = computed(() => {
  return attachment.value.coordinatesLong;
});

const title = computed(() => {
  return attachment.value.fallbackTitle ?? attachment.value.fallback_title;
});

const coordinates = computed(() => `${lat.value}, ${long.value}`);

const mapUrl = computed(
  () => `https://maps.google.com/?q=${lat.value},${long.value}`
);

const copied = ref(false);

async function copyCoordinates() {
  try {
    await navigator.clipboard.writeText(coordinates.value);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = coordinates.value;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 2000);
  }
}
</script>

<template>
  <BaseAttachmentBubble
    icon="i-ph-navigation-arrow-fill"
    icon-bg-color="bg-[#0D9B8A]"
    sender-translation-key="CONVERSATION.SHARED_ATTACHMENT.LOCATION"
    :content="title || ''"
    :action="{
      label: t('COMPONENTS.LOCATION_BUBBLE.SEE_ON_MAP'),
      href: mapUrl,
    }"
  >
    <!-- Override default slot to add coordinates below the title -->
    <div v-if="title" class="truncate text-sm text-n-slate-11">
      {{ title }}
    </div>
    <!-- Coordinates with click-to-copy -->
    <div
      class="flex items-center gap-2 mt-1 px-2 py-1.5 rounded-lg bg-n-alpha-2 border border-n-weak cursor-pointer hover:bg-n-alpha-3 transition-colors group"
      role="button"
      :title="
        copied
          ? t('COMPONENTS.LOCATION_BUBBLE.COPIED')
          : t('COMPONENTS.LOCATION_BUBBLE.COPY_COORDINATES')
      "
      @click="copyCoordinates"
    >
      <span class="i-lucide-map-pin size-3.5 text-n-slate-10 shrink-0" />
      <span class="text-xs font-mono text-n-slate-11 select-all truncate">
        {{ coordinates }}
      </span>
      <span
        v-if="!copied"
        class="i-lucide-copy size-3.5 text-n-slate-10 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
      />
      <span v-else class="flex items-center gap-1 shrink-0">
        <span class="i-lucide-check size-3.5 text-green-500" />
        <span class="text-xs text-green-500 font-medium">
          {{ t('COMPONENTS.LOCATION_BUBBLE.COPIED') }}
        </span>
      </span>
    </div>
  </BaseAttachmentBubble>
</template>
